/**
 * Service layer — Plan Check domain (Supabase-backed)
 *
 * Owns: plan_check_letters, plan_check_comments, stakeholder_inputs
 * (plan_check_rounds and plan_check_lessons are backend-only for now)
 *
 * Delta Revisions stay in Airtable — see getDeltaRevisionsForProject() in permits.js.
 *
 * All calls are Clerk-authed via window.__clerkGetToken().
 */

async function authToken() {
  return window.__clerkGetToken ? await window.__clerkGetToken() : null
}

async function apiFetch(path, opts = {}) {
  const token = await authToken()
  const res = await fetch(path, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
      ...(opts.headers || {}),
    },
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error || res.statusText)
  return json
}

function post(body) {
  return apiFetch('/api/plan-check', { method: 'POST', body: JSON.stringify(body) })
}

/**
 * Fetch all plan-check letters (with embedded comments) and stakeholder inputs
 * for an agency review. Returns { letters, stakeholderInputs }.
 * @param {string} reviewId — Agency Approval (Agency Review) Airtable record id
 */
export async function getPlanCheckData(reviewId) {
  return apiFetch('/api/plan-check?reviewId=' + encodeURIComponent(reviewId))
}

// ── Letters ──────────────────────────────────────────────────────────────────

/**
 * @param {string} reviewId
 * @param {{ date_received?, reviewer_contact_name?, source_letter_url?, notes? }} fields
 */
export function createPlanCheckLetter(reviewId, fields = {}) {
  return post({ action: 'createLetter', reviewId, fields })
}

/** @param {string} letterId @param {object} fields */
export function updatePlanCheckLetter(letterId, fields) {
  return post({ action: 'updateLetter', letterId, fields })
}

/** @param {string} letterId */
export function deletePlanCheckLetter(letterId) {
  return post({ action: 'deleteLetter', letterId })
}

// ── Comments ─────────────────────────────────────────────────────────────────

/**
 * @param {string} letterId
 * @param {{ type?, discipline?, comment_number?, subject?, comment_text?, response_text? }} fields
 */
export function createPlanCheckComment(letterId, fields = {}) {
  return post({ action: 'createComment', letterId, fields })
}

/** @param {string} commentId @param {object} fields */
export function updatePlanCheckComment(commentId, fields) {
  return post({ action: 'updateComment', commentId, fields })
}

/** @param {string} commentId */
export function deletePlanCheckComment(commentId) {
  return post({ action: 'deleteComment', commentId })
}

// ── Stakeholder Inputs ────────────────────────────────────────────────────────

/**
 * @param {string} reviewId
 * @param {{ date_received?, input_type?, source_name?, source_organization?, summary?, response_text?, addressed?, document_url? }} fields
 */
export function createStakeholderInput(reviewId, fields = {}) {
  return post({ action: 'createStakeholderInput', reviewId, fields })
}

/** @param {string} id @param {object} fields */
export function updateStakeholderInput(id, fields) {
  return post({ action: 'updateStakeholderInput', id, fields })
}

/** @param {string} id */
export function deleteStakeholderInput(id) {
  return post({ action: 'deleteStakeholderInput', id })
}

// ── Intake (AI-assisted letter processing) ────────────────────────────────────

function intakePost(body) {
  return apiFetch('/api/plan-check-intake', { method: 'POST', body: JSON.stringify(body) })
}

/** Returns the in-progress (reviewing) intake for a review, or null. */
export function getIntakeForReview(reviewId) {
  return intakePost({ action: 'getForReview', reviewId })
}

/**
 * Get a signed Supabase Storage upload URL for an intake PDF.
 * @returns {{ signedUrl, path, publicUrl }}
 */
export function getPCIntakeUploadUrl(reviewId, filename) {
  return intakePost({ action: 'getUploadUrl', reviewId, filename })
}

/**
 * Upload a PDF to Supabase Storage directly from the browser using a signed URL.
 * @param {string} signedUrl
 * @param {File} file
 */
export async function uploadIntakePdf(signedUrl, file) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/pdf' },
    body: file,
  })
  if (!res.ok) throw new Error('Upload failed: ' + res.status)
}

/**
 * Run Claude extraction on an uploaded PDF. Creates (or updates) an intake row.
 * @returns {{ intake }} where intake = { id, items, backcheck_available, backcheck_notes, letter_date, ... }
 */
export function processPCIntake({ reviewId, projectId, storagePath, pdfUrl, intakeId }) {
  return intakePost({ action: 'process', reviewId, projectId, storagePath, pdfUrl, intakeId })
}

/** Auto-save intake items and metadata. */
export function savePCIntake(intakeId, items, meta) {
  return intakePost({ action: 'save', intakeId, items, meta })
}

/** Discard (delete) an in-progress intake. */
export function discardPCIntake(intakeId) {
  return intakePost({ action: 'discard', intakeId })
}

/**
 * Commit an intake: creates plan_check_letter + plan_check_comments, marks intake committed.
 * @returns {{ letterId: string }}
 */
export function commitPCIntake(intakeId) {
  return intakePost({ action: 'commit', intakeId })
}

// ── PDF generation ────────────────────────────────────────────────────────────

/**
 * Generate a Plan Check Response Letter PDF and return a Blob for download.
 * @param {{ letterId, source, projectId, reviewId, responseDate? }} opts
 */
export async function generatePCResponsePdf({ letterId, source, projectId, reviewId, responseDate }) {
  const token = await authToken()
  const res = await fetch('/api/plan-check-response-pdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: 'Bearer ' + token } : {}),
    },
    body: JSON.stringify({ letterId, source, projectId, reviewId, responseDate }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || 'Failed to generate PDF')
  }
  return res.blob()
}
